import { useEffect, useState } from "react";
import client from "../api/client";

const styles = {
  page: { fontSize: 11, minHeight: "100%", overflow: "visible", padding: 0 },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  info: {
    fontSize: 11,
    padding: "7px 10px",
    borderRadius: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
  },
  tableWrap: {
    maxHeight: 430,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 900, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function StockLedgerPage() {
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadItems = async () => {
    const res = await client.get("/inventory/items");
    setItems(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    loadItems();
  }, []);

  const load = async () => {
    if (!itemId) return alert("Please select item");

    try {
      setLoading(true);
      const res = await client.get(`/inventory/stock-ledger/${itemId}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to load stock ledger");
    } finally {
      setLoading(false);
    }
  };

  const selectedItem = items.find(
    (x) => String(x.Id ?? x.id) === String(itemId),
  );

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-archive me-2"></i>
            Stock Ledger
          </h6>
          <div style={styles.sub}>
            Track item-wise stock movement with running quantity
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2 align-items-end">
            <div className="col-md-5">
              <label style={styles.label}>Item</label>

              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={itemId}
                onChange={(e) => setItemId(e.target.value)}
              >
                <option value="">Select Item</option>
                {items.map((item) => (
                  <option key={item.Id ?? item.id} value={item.Id ?? item.id}>
                    {(item.ItemCode ?? item.itemCode ?? "") +
                      " - " +
                      (item.ItemName ?? item.itemName ?? "")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={styles.btn}
                onClick={load}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>
            </div>
          </div>

          <div className="mb-2" style={styles.info}>
            <strong>Item:</strong>{" "}
            {selectedItem
              ? `${selectedItem.ItemCode ?? selectedItem.itemCode} - ${
                  selectedItem.ItemName ?? selectedItem.itemName
                }`
              : "-"}
            {" | "}
            <strong>HSN:</strong>{" "}
            {selectedItem?.HSNCode ?? selectedItem?.hsnCode ?? "-"}
            {" | "}
            <strong>GST:</strong>{" "}
            {selectedItem?.GSTPercent ?? selectedItem?.gstPercent ?? 0}%
          </div>

          <div
            style={{
              fontSize: 10,
              color: "#6c757d",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>
              Ledger Entries: <strong>{rows.length}</strong>
            </span>
            <span>Stock movement report</span>
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    ["Date", 110, ""],
                    ["Type", 130, ""],
                    ["Qty In", 100, "text-end"],
                    ["Qty Out", 100, "text-end"],
                    ["Rate", 100, "text-end"],
                    ["Ref No", 160, ""],
                    ["Running Qty", 130, "text-end"],
                  ].map(([h, w, cls]) => (
                    <th
                      key={h}
                      style={{ ...styles.th, minWidth: w }}
                      className={cls}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No stock ledger entries found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={i}>
                      <td style={styles.td}>
                        {String(r.TranDate ?? r.tranDate ?? "").substring(
                          0,
                          10,
                        )}
                      </td>
                      <td style={styles.td}>{r.TranType ?? r.tranType}</td>
                      <td className="text-end" style={styles.td}>
                        {Number(r.QtyIn ?? r.qtyIn ?? 0).toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(r.QtyOut ?? r.qtyOut ?? 0).toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(r.Rate ?? r.rate ?? 0).toFixed(2)}
                      </td>
                      <td style={styles.td}>{r.RefNo ?? r.refNo}</td>
                      <td
                        className="text-end"
                        style={{
                          ...styles.td,
                          fontWeight: 700,
                          color: "#0d6efd",
                        }}
                      >
                        {Number(r.RunningQty ?? r.runningQty ?? 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
